import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
// import { PASSWORDLENGTH } from "../LoginPages/Login";
import { useAuth } from "../../services/AuthContext";
import Tooltip from "../../components/Tooltip";
import {
  fetchAccountInfo,
  fetchFamilyInterestRate,
  updateAccountInfo,
  updateFamilyInterestRates,
  fetchChildren,
} from "../../services/Api";
import ChildSelectionPopup from "./ChildSelectionPopup";
import PopupWrapper from "../../components/PopupWrapper";
import { useToast } from "../../services/ToastContext";
import { Eye, EyeOff } from "lucide-react";
import EditChildForm from '../SettingsPages/EditChildForm';
import AddChildForm from '../SettingsPages/AddChildForm';
import ChangePasswordForm from '../SettingsPages/ChangePasswordForm';
import { useRefresh } from "../../services/RefreshContext";

const AccountInfoForm = ({ isOpen, onClose }) => {
  //sets some functions and states
  const { username, guardianID, familyID, checkAuth } = useAuth();
  const { showToast } = useToast();
  const { refreshCounter, triggerRefresh } = useRefresh();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [birthdate, setBirthdate] = useState("");
  const [pin, setPin] = useState("");
  const [children, setChildren] = useState("");
  const [familyName, setFamilyName] = useState("");
  const [primaryInterestRate, setPrimaryInterestRate] = useState("");
  const [secondaryInterestRate, setSecondaryInterestRate] = useState("");
  const [maxPrimaryInterestValue, setMaxPrimaryInterestValue] = useState("");
  const [isChildSelectPopupOpen, setIsChildSelectPopupOpen] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPin, setShowPin] = useState(false);

  const [isEditChildOpen, setIsEditChildOpen] = useState(false);
  const [isAddChildOpen, setIsAddChildOpen] = useState(false);
  const [selectedChildID, setSelectedChildID] = useState(null);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [showAdvancedInterest, setShowAdvancedInterest] = useState(false);


  //These 3 variables will be passed to add child so if interest rate is changed,
  // but not submitted, the values wont change
  const addChildPagePrimaryInterestRate = primaryInterestRate;
  const addChildPageSecondaryInterestRate = secondaryInterestRate;
  const addChildPageMaxPrimaryInterest = maxPrimaryInterestValue;

  const togglePinVisibility = () => setShowPin(!showPin);

  const [inputErrors, setInputErrors] = useState({
    input: "",
    inputPassword: "",
    inputRepeatPassword: "",
    inputPin: "",
  });

  // get user info when page loads
  useEffect(() => {
    if (!username) {
      return;
    }

    const fetchData = async () => {
      try {

        // Fetch all data in parallel
        const [accountInfoRes, interestRateRes, childrenRes] = await Promise.all([
          fetchAccountInfo(username),
          fetchFamilyInterestRate(guardianID),
          fetchChildren(familyID)
        ]);

        const accountInfo = accountInfoRes.accountInfo;
        setFirstName(accountInfo.FirstName);
        setLastName(accountInfo.LastName);
        setEmail(accountInfo.Email);
        setFamilyName(accountInfo.FamilyName);
        setBirthdate(accountInfo.BirthDate);
        setPin(accountInfo.pin);

        const interestInfo = interestRateRes.interestResults;
        setPrimaryInterestRate(interestInfo.InitialRate);
        setSecondaryInterestRate(interestInfo.EndingRate);
        setMaxPrimaryInterestValue(interestInfo.InitialMaximum);

        setChildren(childrenRes);

      } catch (error) {
        console.error("Error fetching account data:", error);
        // Handle error appropriately
      } finally {
        setIsLoading(false); // Add loading state if you want to show a spinner
      }
    };

    fetchData();
  }, [username, guardianID, familyID, refreshCounter, isOpen]);

  useEffect(() => {
    // Reset form when closed
    return () => {
      if (!isOpen) {
        setIsChangePasswordOpen(false);
        setIsChildSelectPopupOpen(false);
        setIsEditChildOpen(false);
        setIsAddChildOpen(false);
      }
    };
  }, [isOpen]);

  // Validation function
  const validateEditAccountForm = () => {
    let valid = true;
    const newErrors = {};

    if (
      !firstName ||
      !lastName ||
      !email ||
      !birthdate ||
      !primaryInterestRate ||
      !secondaryInterestRate ||
      !maxPrimaryInterestValue
    ) {
      newErrors.input = "All fields are required";
      valid = false;
    }

    if (!pin || pin.length < 4) {
      newErrors.inputPin = "Pin must be exactly 4 numbers";
      valid = false;
    }

    setInputErrors(newErrors);
    return valid;
  };

  //function that is called when login button is submitted
  const handleAccountInfoEdit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    if (!validateEditAccountForm()) {
      setIsLoading(false);
      return;
    }

    try {
      //calls post method change info
      const response = await updateAccountInfo(
        firstName,
        lastName,
        email,
        birthdate,
        pin,
        familyName,
        guardianID,
        familyID
      );

      if (response.token) {
        await checkAuth();
      }

      //calls post method to change interest Info
      await updateFamilyInterestRates(
        primaryInterestRate,
        secondaryInterestRate,
        maxPrimaryInterestValue,
        guardianID
      );

      triggerRefresh();
      showToast("Account Info Updated");
      onClose();
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddChild = (e) => {
    e.preventDefault();
    setIsAddChildOpen(true);
  };

  const handleSelectChildToEdit = (childID) => {
    setIsChildSelectPopupOpen(false);
    setSelectedChildID(childID);
    setIsEditChildOpen(true);
  };

  const handlePopupSelectChild = () => {
    setIsChildSelectPopupOpen(true);
  };

  const handleChangePassword = (e) => {
    e.preventDefault();
    setIsChangePasswordOpen(true);
  };

  if (!isOpen) return null;

  return (
    <>
      <PopupWrapper
        isOpen={isOpen}
        onClose={onClose}
        title="Edit Account Information"
      >
        <form className="user">
          <div className="form-group text-left">
            <label
              htmlFor="emailInputEditAccount"
              className="d-block font-weight-bold mb-1 ms-2"
            >
              Email Address:
            </label>
            <input
              type="email"
              className="form-control form-control-user"
              id="emailInputEditAccount"
              placeholder="Email Address"
              onChange={(e) => setEmail(e.target.value)}
              value={email || ""}
            />
          </div>
          <div className="form-group text-left">
            <label
              htmlFor="firstNameInputEditAccount"
              className="d-block font-weight-bold mb-1 ms-2"
            >
              First Name:
            </label>
            <input
              type="text"
              className="form-control form-control-user"
              id="firstNameInputEditAccount"
              placeholder="First Name"
              onChange={(e) => setFirstName(e.target.value)}
              value={firstName || ""}
            />
          </div>
          <div className="form-group text-left">
            <label
              htmlFor="lastNameInputEditAccount"
              className="d-block font-weight-bold mb-1 ms-2"
            >
              Last Name:
            </label>
            <input
              type="text"
              className="form-control form-control-user"
              id="lastNameInputEditAccount"
              placeholder="Last Name"
              onChange={(e) => setLastName(e.target.value)}
              value={lastName || ""}
            />
          </div>
          <div className="form-group text-left">
            <label
              htmlFor="familyNameInputEditAccount"
              className="d-block font-weight-bold mb-1 ms-2"
            >
              Family Name:
            </label>
            <input
              type="text"
              className="form-control form-control-user"
              id="familyNameInputEditAccount"
              placeholder="Family Name"
              maxLength="32"
              onChange={(e) => setFamilyName(e.target.value)}
              value={familyName || ""}
            />
          </div>
          <div className="form-group text-left">
            <label
              htmlFor="birthdateInputEditAccount"
              className="d-block font-weight-bold mb-1 ms-2"
            >
              Birthdate:
            </label>
            <input
              type="date"
              className="form-control form-control-user"
              id="birthdateInputEditAccount"
              max={new Date().toISOString().split("T")[0]} // Set max date to today
              value={birthdate}
              onChange={(e) => setBirthdate(e.target.value)}
            />
          </div>
          <div className="form-group text-left">
            <label
              htmlFor="pinInputEditAccount"
              className="d-block font-weight-bold mb-1 ms-2"
            >
              4-Digit PIN:
            </label>
            <div className="position-relative">
              <input
                type={showPin ? "text" : "password"}
                className="form-control form-control-user"
                id="pinInputEditAccount"
                placeholder="4-Digit PIN"
                maxLength="4" // Limit to 4 digits
                onInput={(e) => {
                  // Allow only numbers (0-9)
                  e.target.value = e.target.value.replace(/[^0-9]/g, "");
                }}
                onChange={(e) => setPin(e.target.value)}
                value={pin || ""}
              />
              <span
                className="passwordToggle"
                onClick={togglePinVisibility}
                style={{ cursor: "pointer" }}
              >
                {showPin ? <EyeOff /> : <Eye />}
              </span>
            </div>
          </div>
          {inputErrors.inputPin && (
            <p className="text-danger">{inputErrors.inputPin}</p>
          )}
          <div className="form-group text-left">
            <div className="d-inline-flex align-items-center">
              <label
                htmlFor="initialFamilyInterestInputCreateAccount"
                className="font-weight-bold mb-1 ms-2"
              >
                Primary Family Interest Rate (Monthly Rate):
              </label>
              <div className="mt-n1">
                <Tooltip
                  content="?"
                  description="Encourage your child to save! At the end of each month, you—the parent—pay interest on their balance at the rate you set, teaching them the power of compound interest. For example, with a 10% family interest rate, a $100 balance would grow to $110. You can adjust this rate anytime in account settings."
                />
              </div>
            </div>
            <input
              type="number"
              className="form-control form-control-user"
              id="initialFamilyInterestInputCreateAccount"
              placeholder="Monthly Rate Percentage"
              min="0"
              onChange={(e) => setPrimaryInterestRate(e.target.value)}
              onInput={(e) =>
                (e.target.value = e.target.value.replace(/[^0-9]/g, ""))
              }
              value={primaryInterestRate || ""}
            />
          </div>


          {/* Advanced Interest Toggle */}
          <button
            type="button"
            className="confirm-btn"
            onClick={() => setShowAdvancedInterest(!showAdvancedInterest)}
          >
            {showAdvancedInterest ? "▼   Hide Advanced Interest" : " ▶ Show Advanced Interest"}
          </button>
          <br />


          {showAdvancedInterest && (
            <>
              <br />
              <div className="form-group text-left">
                <div className="d-inline-flex align-items-center">
                  <label
                    htmlFor="interestCapAmntInputEditAccount"
                    className="d-block font-weight-bold mb-1 ms-2"
                  >
                    Primary Interest Rate Dollar Cap:
                  </label>
                  <div className="mt-n1">
                    <Tooltip
                      content="?"
                      description="This is the maximum balance that earns the full interest rate. Any amount above this cap will earn interest at a lower rate instead. For example, if your max cap is $100 and your child has $150 saved, the first $100 will earn the full interest rate, while the extra $50 will earn the lower secondary rate."
                    />
                  </div>
                </div>
                <input
                  type="number"
                  className="form-control form-control-user"
                  id="interestCapAmntInputEditAccount"
                  placeholder="Primary Interest Rate Dollar Cap"
                  onChange={(e) => setMaxPrimaryInterestValue(e.target.value)}
                  value={maxPrimaryInterestValue || ""}
                />
              </div>




              <div className="form-group text-left">
                <div className="d-inline-flex align-items-center">
                  <label
                    htmlFor="lowerInterestInputEditAccount"
                    className="d-block font-weight-bold mb-1 ms-2"
                  >
                    Secondary Interest Rate (Monthly Rate):
                  </label>
                  <div className="mt-n1">
                    <Tooltip
                      content="?"
                      description="This is the interest rate for any money your child saves beyond the primary interest dollar cap. Since higher balances can grow quickly, this rate is usually lower."
                    />
                  </div>
                </div>
                <input
                  type="number"
                  className="form-control form-control-user"
                  id="lowerInterestInputEditAccount"
                  placeholder="Secondary Monthly Interest Rate"
                  onChange={(e) => setSecondaryInterestRate(e.target.value)}
                  value={secondaryInterestRate || ""}
                />
              </div>
            </>
          )}


          <br /><hr />



          {inputErrors.input && (
            <p className="text-danger">{inputErrors.input}</p>
          )}
          {error && <p className="text-danger">{error}</p>}
          <br />
          <button
            onClick={handleAccountInfoEdit}
            className={`confirm-btn p-3 ${isLoading ? "opacity-50 cursor-not-allowed" : ""
              }`}
            style={{ width: "80%" }}
            disabled={isLoading}
          >
            {isLoading ? "Updating Account..." : "Update Account"}
          </button>
        </form><br />
        <button
          onClick={handleChangePassword}
          className="editing-btn p-3 m-2"
        >
          Change Password
        </button>
        <button
          onClick={handleAddChild}
          className="editing-btn p-3 m-2"
        >
          Add Child
        </button>
        <button
          onClick={handlePopupSelectChild}
          className="editing-btn p-3 m-2"
        >
          Edit Child
        </button>
      </PopupWrapper>

      <ChangePasswordForm
        isOpen={isChangePasswordOpen}
        onClose={() => { setIsChangePasswordOpen(false); }}
      />

      <ChildSelectionPopup
        isOpen={isChildSelectPopupOpen}
        onClose={() => { setIsChildSelectPopupOpen(false) }}
        childList={children}
        title={"Which child do you want to edit?"}
        onSelectChild={handleSelectChildToEdit}
      />

      <EditChildForm
        isOpen={isEditChildOpen}
        onClose={() => { setIsEditChildOpen(false); setSelectedChildID(null); }}
        title={"Edit Your Child's Info"}
        childID={selectedChildID}
      />

      <AddChildForm
        isOpen={isAddChildOpen}
        onClose={() => { setIsAddChildOpen(false); }}
        guardianID={guardianID}
        familyName={familyName}
        email={email}
        primaryRate={addChildPagePrimaryInterestRate}
        secondaryRate={addChildPageSecondaryInterestRate}
        cappedPrimaryInterestValue={addChildPageMaxPrimaryInterest}
        doCreateToken={false}
      />

    </>
  );
};

export default AccountInfoForm;
